<template>
  <div class="brochure-single-page-display">
    <!-- Prev spread button -->
    <BrochureButton
      v-if="pagenumber > 0 && isEmbed"
      direction="back"
      class="nav-button back allow-touch"
      @click="pagenumber--"
      @dblclick.prevent.stop
    />
    <div class="carousel-container">
      <PanZoom
        v-model:zoom-level="zoomLevel"
        :is-active="isPanZoomActive && !isEmbed"
      >
        <div ref="carousel" class="carousel">
          <template
            v-for="page in pagesWithLastPage"
            :key="page ? page.id : 'lastpage'"
          >
            <BrochurePageItem
              ref="pagesRef"
              :page="page"
              :hotspot-mode="hotspotMode"
              :is-last-page="!page"
              :carousel="isCarouselLoaded ? carousel : null"
              class="carousel-item single-page"
              :class="{ transparant: !isCarouselLoaded }"
              :on-is-in-view="pageIsInView"
            />
          </template>
        </div>
      </PanZoom>
    </div>
    <!-- Next spread button -->
    <BrochureButton
      v-if="pagenumber !== pages.length - 1 && isEmbed"
      direction="forward"
      class="nav-button forward allow-touch"
      @click="pagenumber++"
      @dblclick.prevent.stop
    />
    <BrochureZoomShare
      v-if="!isEmbed"
      v-model:zoom-level="zoomLevel"
      :brochure-title="brochureTitle"
      :show-zoom="!isAdPage"
      :has-prev="pagenumber !== 0"
      :has-next="pagenumber !== pages.length - 1"
      @first="pagenumber = 0"
      @last="pagenumber = pages.length - 1"
      @next="pagenumber++"
      @prev="pagenumber--"
    />
    <Advertisement :display-at="AdDisplayAt.BrochureViewerInterface" />
  </div>
</template>

<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import BrochurePageItem from "./BrochurePageItem.vue";
import {
  HotspotMode,
  type PageFragment,
  AdDisplayAt,
  useCreatePageTimeStatisticMutation,
  useCreatePageVisitStatisticMutation,
} from "~/graphql/generated";

const props = defineProps<{
  pages: PageFragment[];
  hotspotMode: HotspotMode;
  brochureTitle: string;
}>();

const isEmbed = isEmbedded();

const zoomLevel = ref(1);

const pagenumber = useRouteNumberParam("pagenumber", 0);

// Include a last 'null' page where we display similar brochures
const pagesWithLastPage = computed(() => {
  if (!props.pages?.length || !isCarouselLoaded) return [];
  return [...props.pages, null];
});

// Get the page by index
const getPage = (index: number) => {
  return props.pages.find((page) => page?.index === index) || null;
};
// Get the current spread
const currentPage = computed(() => getPage(pagenumber.value));

/*
 * Scroll / carousel handling
 */
const carousel = ref<HTMLDivElement | null>(null);
const { width: pageWidth } = useWindowSize();

const { x: carouselScrollX, isScrolling } = useScroll(carousel, {
  behavior: "instant",
});

// Check the carousel load state with in interval, because the scrollWidth is not reactive
const isCarouselLoaded = ref(false);
const loadStateInterval = setInterval(() => {
  if (isCarouselLoaded.value) {
    clearInterval(loadStateInterval);
  }

  // if the scrollWidth is larger then or equal to the pageWidth * the number of pages
  // the carousel is fully loaded
  const isLoaded =
    (carousel.value?.scrollWidth ?? 0) >= pageWidth.value * props.pages.length;

  if (!isLoaded) return;

  carouselScrollX.value = pagenumber.value * pageWidth.value;

  isCarouselLoaded.value = true;
}, 50);

// Watch the pagenumber change
watch(
  pagenumber,
  () => {
    if (!isCarouselLoaded.value || isScrolling.value) return;
    zoomLevel.value = 1;
    carouselScrollX.value = pagenumber.value * pageWidth.value;
  },
  { immediate: true }
);

const isAdPage = ref(false);

// Event called by the page item when it is scrolled into view (IntersectionObserver)
const pageIsInView = (pageId: number | null) => {
  // If the carousel is not loaded, ignore the event
  if (!isCarouselLoaded.value) return;
  if (pageId === null) {
    // If we get null, it means we have reached the last page with ad and related brochures
    isAdPage.value = true;
  } else {
    isAdPage.value = false;
    pagenumber.value = pageId;
  }
};

/*
 * Panzoom is only active when
 * 1. There is no active hotspot modal
 * 2. There is no scroll activity
 */
const activeHotspot = useRouteQuery("offer");
const isPanZoomActive = computed(() => {
  return !activeHotspot.value && !isScrolling.value && !isAdPage.value;
});

// Set a scrollock when zoomed in
const scrollLock = useScrollLock(carousel);
watch(zoomLevel, (zoom) => {
  scrollLock.value = zoom > 1;
});

/*
 * The Page statistic mutations
 */
const deviceId = useDeviceId();
const createPageVisit = useCreatePageVisitStatisticMutation();
const createPageTimeStatistic = useCreatePageTimeStatisticMutation();

// Time components
const { startTimer, pauseTimer, resumeTimer, resetTimer, getTimeInSeconds } =
  useTimer();
startTimer();
const windowFocus = useWindowFocus();
watch(windowFocus, (isFocused) => {
  if (!isFocused) pauseTimer();
  if (isFocused) resumeTimer();
});
watch(
  pagenumber,
  (_, prevPageNumber) => {
    if (currentPage.value) {
      createPageVisit.executeMutation({
        createPageVisitStatistic: {
          deviceId: deviceId.value,
          pageId: currentPage.value?.id,
        },
      });
    }

    if (prevPageNumber) {
      const prevPageVisited = getPage(prevPageNumber);

      if (prevPageVisited) {
        createPageTimeStatistic.executeMutation({
          createPageTimeStatistic: {
            deviceId: deviceId.value,
            pageId: prevPageVisited.id,
            timeInSeconds: getTimeInSeconds(),
          },
        });
      }
      resetTimer();
    }
  },
  { immediate: true }
);

// Also (try to) send a statistics mutation event on the beforeunload (window close) event
onMounted(() => {
  useEventListener(document, "beforeunload", () => {
    if (currentPage.value) {
      createPageTimeStatistic.executeMutation({
        createPageTimeStatistic: {
          deviceId: deviceId.value,
          pageId: currentPage.value.id,
          timeInSeconds: getTimeInSeconds(), // Divide by two pages
        },
      });
    }
  });
});
</script>

<style lang="scss" scoped>
.brochure-single-page-display {
  height: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
}

.brochure-single-page-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.carousel-container {
  width: 100vw;
  height: 100%;
  overflow: hidden;
  .carousel {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    height: 100%;
    width: 100vw;
    scroll-snap-type: x mandatory;

    scroll-behavior: smooth;

    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */

    &::-webkit-scrollbar {
      display: none; /* For Chrome, Safari and Opera */
    }

    .carousel-item {
      scroll-snap-align: center;
      scroll-snap-stop: always;
      width: 100vw;

      &.transparant {
        opacity: 0;
      }
    }
  }
}

.nav-button {
  position: absolute;
  top: 50%;
  z-index: 4;

  &.back {
    left: 1.5rem;
  }

  &.forward {
    right: 1.5rem;
  }
}
</style>
